import {
  Component, OnDestroy, OnInit, inject, ViewChild, ElementRef, ChangeDetectorRef
} from '@angular/core';
import {afterNextRender} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule} from '@angular/forms';
import {AuthenticationService} from '../_services/authentication.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {catchError, filter, first, take, takeUntil, tap} from 'rxjs/operators';
import {I18nService} from '../_services/gui/i18n.service';
import {TabBarService} from '../_services/gui/tab-bar.service';
import {concat, of, Subject, timer} from 'rxjs';
import {containsIgnoreCase, isBlank} from '../_utils/lang';
import {JwtToken, RawDecodedTokenPair} from '../_data/admin/jwt-token';
import {DeviceDetails, UserDetails} from '../_data/mfa/mfa-user-details';
import {SecurityService} from '../_services/data/security.service';
import {AppConfig} from '../_data/config/app-config';
import {exists} from '../_utils/operators';
import {NotificationService} from '../_services/gui/notification.service';
import * as _ from 'lodash';
import {isRawDecodedTokenPair} from '../_utils/type-guards';
import {NgbTooltip} from '@ng-bootstrap/ng-bootstrap/tooltip';
import {NgClass} from '@angular/common';
import {ValidationErrorsComponent} from '../common/forms/validation-errors/validation-errors.component';
import {AuthenticateComponent} from '../mfa/authenticate/authenticate.component';
import {AddMfaDeviceComponent} from '../mfa/add-mfa-device/add-mfa-device.component';
import {TranslateModule} from '@ngx-translate/core';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    NgbTooltip,
    ReactiveFormsModule,
    NgClass,
    ValidationErrorsComponent,
    RouterLink,
    AuthenticateComponent,
    AddMfaDeviceComponent,
    TranslateModule
  ]
})
export class LoginComponent implements OnInit, OnDestroy {
  
  @ViewChild('passwordInput') passwordInput?: ElementRef<HTMLInputElement>;

  loginFailed = false;
  loginAccessFailed = false;
  loginLocked = false;
  initialMfaDevice: DeviceDetails;
  userName: string;
  jwtToken: RawDecodedTokenPair<UserDetails>;

  readonly appConfig = inject(AppConfig);

  loginForm: UntypedFormGroup;
  showFormErrors = false;

  private readonly formBuilder = inject(UntypedFormBuilder);

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly securityService = inject(SecurityService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly i18nService = inject(I18nService);
  private readonly tabBarService = inject(TabBarService);
  private readonly notificationService = inject(NotificationService);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly returnUrl: string;

  private _showMfaAuthenticate: boolean;
  private _showMfaAddDevice: boolean;
  private _showMfaDeviceAdded: boolean;
  private _showUsernameForm: boolean;
  private _showPasswordForm: boolean;
  private _showForwardWarning: boolean;

  private destroy$ = new Subject<void>();

  constructor() {
    this.returnUrl = this.route.snapshot.queryParams.returnUrl;

    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });

    this.route.data
      .pipe(exists(), filter((value) => value.logout))
      .subscribe(() => {
        console.info('Login route told to log out');
        this.logout();
      });
  }

  private initDisplayStates() {
    console.debug('initializing login display');
    this.resetLoginForm();
  }

  private needsDeviceAdded(): boolean {
    const authNPending = this.jwtToken?.decodedToken?.authNContext === 'MFA_PENDING_SETUP';
    const hasValidDeviceId = this.jwtToken?.token?.mfaUserDetails?.deviceDetails?.deviceId > 0;
    const hasDevicesValues = (this.jwtToken?.token?.mfaUserDetails?.devicesDetails?.length ?? 0) > 0;
    return !!authNPending || (!hasValidDeviceId && !hasDevicesValues);
  }

  private showMfaForLogin(tokens: RawDecodedTokenPair<UserDetails>) {
    this.jwtToken = tokens;
    if(this.needsDeviceAdded()) {
      this.showMfaAddDevice = true;
      console.debug(`User has no MFA devices`);
      this.cdr.detectChanges();
    } else if(this.jwtToken.decodedToken.authNContext === 'MFA_ACTION_PENDING') {
      this.initialMfaDevice = this.jwtToken?.token?.mfaUserDetails?.deviceDetails;
      this.showMfaAuthenticate = true;
      this.cdr.detectChanges();
    } else {
      console.warn('Unexpected MFA state', this.jwtToken.decodedToken.authNContext);
      this.notificationService.displayI18n('content.error-occurred');
      this.showUsernameForm = true;
      this.cdr.detectChanges();
    }
  }

  private isLoginSuccess(tokens: RawDecodedTokenPair<UserDetails> | number) {
    if(typeof tokens != 'number') {
      const loginSuccess = !!tokens?.token;
      this.loginFailed = !loginSuccess;
      if(this.loginFailed) console.info('Login failed');
      return loginSuccess;
    } else if(_.isFinite(tokens)) {
      if(tokens === 418) {
        this.loginAccessFailed = true;
        console.info('Login Access failed due to time restriction');
        return false;
      } else if(tokens === 423) {
        this.loginLocked = true;
        console.log('Login failed due to account locked');
        return false;
      } else {
        this.loginFailed = true;
        console.info('Login failed');
        return false;
      }
    }
  }

  ngOnInit() {
    this.i18nService.setTitle('title.login');
    this.tabBarService.hide();
    this.tabBarService.hideGuideLink();

    this.initDisplayStates();

    this.userName = null;
    this.jwtToken = null;

    this.authenticationService.isFirstTimeUser$
      .pipe(
        filter(isFirstTimeUser => isFirstTimeUser),
        takeUntil(this.destroy$),
        take(1)
      )
      .subscribe(() =>
        this.router.navigateByUrl('/first-time-login')
          .then(v => console.debug(`Redirected to first-time-login: ${v}`))
      );

    this.authenticationService.logout$.subscribe(() => this.initDisplayStates());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  resetLoginForm() {
    this.loginForm?.reset();
    this.showFormErrors = false;
    this.loginFailed = false;
    this.showUsernameForm = true;
    this.loginAccessFailed = false;
    this.loginLocked = false;
    this.cdr.detectChanges();
  }

  public submitUsername(): void {
    if(this.loginForm.controls.username.valid) {
      this.securityService.userMigrated(this.loginForm.value.username).subscribe(migrated => {
        if(migrated) {
          this.showPasswordForm = true;
          this.showFormErrors = false;

          // Ensure view updates 
          this.cdr.detectChanges();

          // Focus password after DOM commit
          afterNextRender(() => {
            this.passwordInput?.nativeElement?.focus();
          });
        } else {
          console.info('You will be forwarded to the legacy TU Direct');
          this.showForwardWarning = true;
          this.cdr.detectChanges();

          timer(10000).subscribe(() => window.location.href = this.appConfig.legacyForwardUrl);
        }
      });
    } else {
      this.showFormErrors = true;
      this.cdr.detectChanges();
    }
  }

  public submitLogin(): void {
    console.info('Logging in...');
    this.showFormErrors = true;
    this.cdr.detectChanges();

    if(this.loginForm.valid) {
      const username = this.loginForm.value.username;
      const password = this.loginForm.value.password;

      this.authenticationService.startLogin(username, password).pipe(
        first(),
        catchError(error => {
          console.error('Login error', error);
          return of(null);
        }),
        filter((tokens: RawDecodedTokenPair<UserDetails> | number) => this.isLoginSuccess(tokens)),
        filter(tokens => typeof tokens != 'number'),
        tap(() => this.userName = username)
      ).subscribe((tokens: RawDecodedTokenPair<UserDetails> | number) => {
        if(isRawDecodedTokenPair(tokens)) {
          if(!containsIgnoreCase(tokens?.decodedToken?.user?.status, 'NEW USER')) {
            if(containsIgnoreCase(tokens?.decodedToken?.user?.status, 'PASSWORD EXPIRED')) {
              this.router.navigateByUrl('/password-expired')
                .then(success => console.debug('Navigated to password expired', success));
            } else if(tokens?.decodedToken?.authNContext === 'MFA_BYPASSED') {
              this.finishLogin(tokens.token);
            } else {
              this.showMfaForLogin(tokens);
            }
          }
        }
      });
    }
  }

  public finishLogin(token: JwtToken<any>) {
    this.authenticationService.finishLogin(this.userName, token).subscribe(finishLoginSuccess => {
      if(_.isFinite(finishLoginSuccess)) {
        this.loginAccessFailed = true;
        console.info('Login Access failed due to time restriction');
        this.showFormErrors = true;
        this.cdr.detectChanges();
      } else if(finishLoginSuccess) {
        this.showFormErrors = false;
        this.cdr.detectChanges();

        concat(of(this.returnUrl), this.authenticationService.homePage$).pipe(
          filter(v => !isBlank(v)),
          first()
        ).subscribe(url => {
          console.debug(`Redirect url is ${url}`);
          this.router.navigateByUrl(url).then(v => console.debug(`Redirected to ${url}: ${v}`));
        });
      }
    });
  }

  public onMfaCodeSubmitted(jwtToken: JwtToken<any>) {
    if(jwtToken) {
      this.finishLogin(jwtToken);
    }
  }

  onMfaDevicesChanged() {
    this.showMfaAddDevice = false;
    this.showMfaDeviceAdded = true;
    this.cdr.detectChanges();
  }

  logout() {
    this.authenticationService.logout();
  }

  get showUsernameForm(): boolean {
    return this._showUsernameForm;
  }

  set showUsernameForm(value: boolean) {
    this._showUsernameForm = value;
    if(value) {
      this.showPasswordForm = false;
      this.showForwardWarning = false;
      this.showMfaAuthenticate = false;
      this.showMfaAddDevice = false;
      this.showMfaDeviceAdded = false;
    }
  }

  get showPasswordForm(): boolean {
    return this._showPasswordForm;
  }

  set showPasswordForm(value: boolean) {
    this._showPasswordForm = value;
    if(value) {
      this.showUsernameForm = false;
      this.showForwardWarning = false;
      this.showMfaAuthenticate = false;
      this.showMfaAddDevice = false;
      this.showMfaDeviceAdded = false;
    }
  }

  get showUsernamePasswordForm(): boolean {
    return this.showUsernameForm || this.showPasswordForm || this.showForwardWarning;
  }

  get showForwardWarning(): boolean {
    return this._showForwardWarning;
  }

  set showForwardWarning(value: boolean) {
    this._showForwardWarning = value;
    if(value) {
      this.showPasswordForm = false;
      this.showUsernameForm = false;
      this.showMfaAuthenticate = false;
      this.showMfaAddDevice = false;
      this.showMfaDeviceAdded = false;
    }
  }

  get showMfaAuthenticate(): boolean {
    return this._showMfaAuthenticate;
  }

  set showMfaAuthenticate(value: boolean) {
    this._showMfaAuthenticate = value;
    if(value) {
      this.showPasswordForm = false;
      this.showUsernameForm = false;
      this.showForwardWarning = false;
      this.showMfaAddDevice = false;
      this.showMfaDeviceAdded = false;
    }
  }

  get showMfaAddDevice(): boolean {
    return this._showMfaAddDevice;
  }

  set showMfaAddDevice(value: boolean) {
    this._showMfaAddDevice = value;
    if(value) {
      this.showPasswordForm = false;
      this.showUsernameForm = false;
      this.showForwardWarning = false;
      this.showMfaAuthenticate = false;
      this.showMfaDeviceAdded = false;
    }
  }

  get showMfaDeviceAdded(): boolean {
    return this._showMfaDeviceAdded;
  }

  set showMfaDeviceAdded(value: boolean) {
    this._showMfaDeviceAdded = value;
    if(value) {
      this.showPasswordForm = false;
      this.showUsernameForm = false;
      this.showForwardWarning = false;
      this.showMfaAuthenticate = false;
    }
  }
}
